import { memo, useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { Modal, ModalBody } from "react-bootstrap";
import MainService from "../Services/MainService";
import { MdCancel, MdOutlineTimerOff } from "react-icons/md";
import { IoCheckmarkCircle } from "react-icons/io5";
import { IoMdWallet } from "react-icons/io";
import PaymentDataContext from "../PaymentDataContext";
import { PulseLoader } from "react-spinners";


const getScanUrl = (paymentData) => {
    if (!paymentData || !paymentData?.cryptoTransactionHash || !paymentData?.cryptoNetworkId) return null;
    switch (paymentData.cryptoNetworkId) {
        case 1:
            return <a href={`https://etherscan.io/tx/${paymentData.cryptoTransactionHash}`} target="_blank" rel="noopener noreferrer">
                View TXN on EtherScan
            </a>
        case 5:
            return <a href={`https://bscscan.com/tx/${paymentData.cryptoTransactionHash}`} target="_blank" rel="noopener noreferrer">
                View TXN on BscScan
            </a>
        case 10:
            return <a href={`https://tronscan.org/#/transaction/${paymentData.cryptoTransactionHash}`} target="_blank" rel="noopener noreferrer">
                View TXN on TronScan
            </a>
        case 1013:
            return <a href={`https://arbiscan.io/tx/${paymentData.cryptoTransactionHash}`} target="_blank" rel="noopener noreferrer">
                View TXN on ArbiScan
            </a>
        default:
            return <a href={`https://blockscan.com/tx/${paymentData.cryptoTransactionHash}`} target="_blank" rel="noopener noreferrer">
                View TXN on BlockScan
            </a>
    }
}

// A payment request has been sent to your wallet
const TxPart0 = memo(({ paymentData }) => {
    if (paymentData.status !== 0) return null;

    const paymentTime = paymentData?.paymentRequestedDatetime;
    if (paymentTime === "0001-01-01T00:00:00") { return null; }
    const ptimeNow = MainService.convertUTCDateToLocalDate(new Date(paymentTime));
    const initialSeconds = Math.max(5 * 60 - (Date.now() / 1000 - ptimeNow.getTime() / 1000), 0);

    const [timeRemaining, setTimeRemaining] = useState(initialSeconds);

    useEffect(() => {
        const interval = setInterval(() => {
            setTimeRemaining(prev => Math.max(prev - 1, 0));
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        // сбрасываем, если пришёл новый paymentTime
        const newSeconds = Math.max(5 * 60 - (Date.now() / 1000 - ptimeNow.getTime() / 1000), 0);
        setTimeRemaining(newSeconds);
    }, [paymentTime]);

    // console.log(Math.floor(timeRemaining));


    return (
        <div className="cx-modal-body-wrapper txPart">
            <p><strong className="cx-d-block cx-spiner-text">A payment request has been sent to your wallet</strong></p>
            <div className="mb-2">
                <IoMdWallet color="#0533B3" size={60} />
            </div>
            <p className="cx-d-block">Please accept within</p>
            <strong className="cx-d-block cx-blue-color">
                {MainService.secondsToString(Math.floor(timeRemaining))}
            </strong>
        </div>
    );
});

// Waiting for TXN
const TxPart1 = memo(({ paymentData }) => {
    if (paymentData.status !== 1) return null;
    return (
        <div>
            <div className="cx-modal-body-wrapper txPart">
                <div className="d-flex align-items-end justify-content-center">
                    <strong className="cx-d-block cx-spiner-text">Waiting for TXN</strong>
                    <PulseLoader style={{ marginBottom: "-1px" }} size={3} speedMultiplier={0.5} />
                </div>
                <div className="cx-loader-wrapper">
                    <div className="loader spiner-20"></div>
                </div>
                {getScanUrl(paymentData)}
            </div>
        </div>
    );
});

// Sorry, your transaction is failed!
const TxPart2 = memo(() => (
    <div className="cx-modal-body-wrapper txPart">
        <p><strong className="cx-d-block">Sorry, your transaction is failed!</strong></p>
        <div className="my-2">
            <MdCancel color="#B30505" size={60} />
        </div>
        <Link to="#" className="my-2 d-block">
            Please try again
        </Link>
    </div>
));

// You cancelled or rejected the current transaction!
const TxPart3 = memo(({ paymentData }) => {
    if (paymentData.status !== 3) return null;
    return (
        <div className="cx-modal-body-wrapper txPart">
            <p><strong className="cx-d-block">You cancelled or rejected the current transaction!</strong></p>
            <div className="mb-2">
                <MdCancel color="#B30505" size={60} />
            </div>
            {
                paymentData.cancelUrl ?
                    <Link to="#" onClick={() => window.location.href = paymentData.cancelUrl} className="my-2 d-block">
                        Back to merchant
                    </Link>
                    : null
            }
        </div>
    );
});

// Sorry, your transaction is rejected!
const TxPart4 = memo(() => (
    <div className="cx-modal-body-wrapper txPart">
        <p><strong className="cx-d-block">Sorry, your transaction is rejected!</strong></p>
        <div className="my-2">
            <MdCancel color="#B30505" size={60} />
        </div>
        <Link to="#" className="my-2 d-block">
            Please try again
        </Link>
    </div>
));

// Your Order is Complete!
const TxPart5 = memo(({ paymentData }) => {

    return <div className="cx-modal-body-wrapper txPart">
        <div className="cx-merchant-logo">
            <div id="orgImage"></div>
        </div>
        <div className="cx-modal-body-container">
            <div className="cx-modal-body-wrapper">
                <div className="cx-modal-body">
                    <strong className="cx-window-title">Your Order is Complete!</strong>
                    <div className="cx-modal-main-title">
                        <strong className="orgName">Order: </strong>
                        <span className="orderNum">{paymentData.remoteTransactionId ? paymentData.remoteTransactionId : ""}</span>
                    </div>
                    <div className="my-2"><IoCheckmarkCircle size={60} color="#05B32E" /></div>
                    {getScanUrl(paymentData)}
                    {
                        paymentData?.successUrl ?
                            <p className="cx-d-block cx-info-text">
                                <br />
                                You may now close this window
                                <br />
                                <a href={`${paymentData.successUrl}`} rel="noopener noreferrer">Return to merchant</a>
                            </p>
                            : null
                    }
                </div>
            </div>
        </div>
    </div>
});

// Transaction has been cancelled successfully
const TxPart6 = memo(({ paymentData }) => (
    <div className="cx-modal-body-wrapper txPart">
        <p><strong className="cx-d-block cx-spiner-text">Transaction has been cancelled successfully</strong></p>
        <div className="my-2"><IoCheckmarkCircle size={60} color="#05B32E" /></div>
        {
            paymentData.cancelUrl ?
                <Link to="#" onClick={() => window.location.href = paymentData.cancelUrl} className="my-2 d-block">
                    Back to merchant
                </Link>
                : null
        }
    </div>
));

// Sorry, your transaction request has timed out.
const TxPart7 = memo(() => (
    <div className="cx-modal-body-wrapper txPart">
        <p>
            <strong className="cx-d-block cx-spiner-text">
                Sorry, your transaction request has timed out.
            </strong>
        </p>
        <div className="my-2">
            <MdOutlineTimerOff size={60} color="#B30505" />
        </div>
        <Link to="#" className="my-2 d-block">
            Please try again
        </Link>
    </div>
));



const TransactionWaiting = ({ cancelPayment }) => {

    const { paymentData } = useContext(PaymentDataContext);
    if (!paymentData) return null;
    const returnTxPart = () => {
        switch (paymentData.status) {
            case 0:
                return <TxPart0 paymentData={paymentData} />
            case 1:
                return <TxPart1 paymentData={paymentData} />
            case 2:
                return <TxPart2 />
            case 3:
                return <TxPart3 paymentData={paymentData} />
            case 4:
                return <TxPart4 />
            case 5:
                return <TxPart5 paymentData={paymentData} />
            case 6:
                return <TxPart6 paymentData={paymentData} />
            case 7:
                return <TxPart7 />
            default:
                break;
        }
    }

    return (
        <Modal
            show={paymentData?.status > 0 || paymentData?.paymentRequestedDatetime !== "0001-01-01T00:00:00"}
            size="md"
            centered
        >
            <ModalBody>
                <div className="cx-modal-container">
                    <div className="cx-modal-wrapper">
                        <div className="cx-modal-body-container">
                            {returnTxPart()}
                        </div>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default TransactionWaiting;
